// src/components/blogUtils.js
// Single source of truth for the "Latest News" / News & Blogs experience.
// Every consumer (homepage teaser, InfoCenter listing, SEO tags) derives its
// data from blogPostData here so previews can never drift from the real posts.

import { blogPostData } from './BlogPostData';

export const SITE_BASE_URL = 'https://satoshiadvance.com';
export const CATEGORY_ALL = 'All';

// Turn a post's date into a sortable timestamp. Prefer the machine-readable
// `publishedAt` (ISO); fall back to parsing the free-form `date` string so
// posts without an ISO date still order sensibly instead of jumping to the top.
const toTimestamp = (post) => {
  if (post.publishedAt) {
    const iso = new Date(post.publishedAt);
    if (!Number.isNaN(iso.getTime())) return iso.getTime();
  }
  if (post.date) {
    const parsed = new Date(post.date);
    if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
  }
  return 0; // Unknown dates sink to the bottom rather than pretending to be new.
};

// Pull the first real body paragraph so the card preview matches the article's
// actual opening. Falls back to the pull-quote when there's no prose content.
const firstParagraphOf = (post) => {
  if (Array.isArray(post.content)) {
    const firstRegular = post.content.find(
      (section) => section && section.type === 'regular' && section.text
    );
    if (firstRegular) return firstRegular.text;
  }
  return post.mainQuote || '';
};

const truncate = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
};

// Derive lightweight card previews from the canonical post data, newest first.
// The object KEY is the canonical route slug (matches InfoCenter's lookup),
// which avoids the stale internal `slug` fields that don't always match.
export const getBlogPreviews = () =>
  Object.entries(blogPostData)
    .map(([slug, post]) => {
      const firstParagraph = firstParagraphOf(post);
      return {
        id: post.id,
        slug,
        title: post.title,
        date: post.date,
        publishedAt: post.publishedAt || null,
        timestamp: toTimestamp(post),
        tagline: post.category,
        author: post.authors,
        image: post.headerImage,
        preview: truncate(firstParagraph, 140),
        firstParagraph,
      };
    })
    .sort((a, b) => b.timestamp - a.timestamp);

// Unique category list (for filter chips), with "All" first.
export const getBlogCategories = (previews = getBlogPreviews()) => {
  const categories = [];
  previews.forEach((p) => {
    if (p.tagline && !categories.includes(p.tagline)) categories.push(p.tagline);
  });
  return [CATEGORY_ALL, ...categories];
};

// Filter previews by category + free-text query (title, preview, tagline).
export const filterBlogPreviews = (previews, { category = CATEGORY_ALL, query = '' } = {}) => {
  const q = query.trim().toLowerCase();
  return previews.filter((p) => {
    const matchesCategory = category === CATEGORY_ALL || p.tagline === category;
    if (!matchesCategory) return false;
    if (!q) return true;
    return (
      (p.title && p.title.toLowerCase().includes(q)) ||
      (p.preview && p.preview.toLowerCase().includes(q)) ||
      (p.tagline && p.tagline.toLowerCase().includes(q))
    );
  });
};

// Build SEO metadata (title, description, canonical, OG/Twitter, JSON-LD) for a
// single article. Consumed by InfoCenter's <Helmet>.
export const getArticleSeo = (post, slug) => {
  if (!post) return null;
  const description = truncate(post.mainQuote || firstParagraphOf(post), 155);
  const url = `${SITE_BASE_URL}/info-center/${slug}`;
  const pageTitle = `${post.title} | Satoshi Advance News`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description,
    image: post.headerImage ? [post.headerImage] : undefined,
    datePublished: post.publishedAt || undefined,
    author: post.authors
      ? { '@type': 'Person', name: post.authors }
      : { '@type': 'Organization', name: 'Satoshi Advance' },
    publisher: {
      '@type': 'Organization',
      name: 'Satoshi Advance',
      logo: {
        '@type': 'ImageObject',
        url: 'https://media.satoshiadvance.com/images/SatoshiAdvanceLogo.png',
      },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    articleSection: post.category || undefined,
  };

  return { pageTitle, description, url, image: post.headerImage, jsonLd };
};
